import { useState, useEffect, useRef, useCallback } from 'react';
import api, { formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Upload, Loader2, Image as ImageIcon, Trash2, Pencil, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCat, setEditCat] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/categories');
      setCategories(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (catId, file) => {
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      toast.error('Format non supporté (jpg, png, webp uniquement)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 2 MB)');
      return;
    }
    setUploadingId(catId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/admin/categories/${catId}/image`, fd);
      toast.success('Image mise à jour');
      setCategories(prev => prev.map(c => c.id === catId ? { ...c, image_url: data.image_url } : c));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Erreur upload');
    } finally {
      setUploadingId(null);
      if (fileInputs.current[catId]) fileInputs.current[catId].value = '';
    }
  };

  const handleReset = async () => {
    if (!resetTarget) return;
    try {
      const { data } = await api.delete(`/admin/categories/${resetTarget.id}/image`);
      toast.success('Image réinitialisée');
      setCategories(prev => prev.map(c => c.id === resetTarget.id ? { ...c, image_url: data.image_url } : c));
      setResetTarget(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Erreur');
    }
  };

  const openRename = (cat) => {
    setEditCat(cat);
    setEditName(cat.name);
  };

  const saveRename = async () => {
    if (!editCat || !editName.trim()) return;
    setSaving(true);
    try {
      await api.put(`/admin/categories/${editCat.id}`, { name: editName.trim() });
      toast.success('Nom modifié');
      setCategories(prev => prev.map(c => c.id === editCat.id ? { ...c, name: editName.trim() } : c));
      setEditCat(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="categories-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Catégories</h1>
          <p className="text-sm text-slate-500">
            Gérez les images et noms des {categories.length} catégories affichées sur le site vitrine.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {categories.map(cat => (
            <div
              key={cat.id}
              className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`category-card-${cat.id}`}
            >
              {/* Image */}
              <div className="relative h-44 bg-slate-100 group">
                {cat.image_url ? (
                  <img
                    src={resolveImageUrl(cat.image_url)}
                    alt={cat.name}
                    width={400}
                    height={176}
                    loading="eager"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300">
                    <ImageIcon className="w-12 h-12" />
                  </div>
                )}
                {uploadingId === cat.id && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" /> Upload...
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{cat.name}</h3>
                    <p className="text-xs text-slate-500 font-mono">{cat.slug}</p>
                  </div>
                  <div className="flex items-center text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded gap-1 flex-shrink-0">
                    <Package className="w-3 h-3" />
                    <span data-testid={`category-count-${cat.id}`}>{cat.products_count}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <input
                    ref={(el) => { fileInputs.current[cat.id] = el; }}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => handleFile(cat.id, e.target.files?.[0])}
                    data-testid={`category-file-input-${cat.id}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => fileInputs.current[cat.id]?.click()}
                    disabled={uploadingId === cat.id}
                    className="bg-[#0A3D73] hover:bg-[#082E56] text-white"
                    data-testid={`category-upload-btn-${cat.id}`}
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" /> Modifier l'image
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openRename(cat)}
                    data-testid={`category-rename-btn-${cat.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Renommer
                  </Button>
                  {cat.image_url && cat.image_url.startsWith('/api/uploads/') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      onClick={() => setResetTarget(cat)}
                      data-testid={`category-reset-btn-${cat.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rename dialog */}
      <Dialog open={!!editCat} onOpenChange={(open) => !open && setEditCat(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renommer la catégorie</DialogTitle>
            <DialogDescription>
              L'identifiant interne ({editCat?.slug}) ne change pas — il est utilisé par les produits déjà importés.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="cat-name">Nom affiché</Label>
            <Input
              id="cat-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              data-testid="category-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCat(null)}>Annuler</Button>
            <Button
              onClick={saveRename}
              disabled={saving || !editName.trim()}
              className="bg-[#0A3D73] hover:bg-[#082E56] text-white"
              data-testid="category-save-name-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset confirm */}
      <AlertDialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Réinitialiser l'image ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'image actuelle de "{resetTarget?.name}" sera supprimée et remplacée par l'image par défaut.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reset-image">
              Réinitialiser
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
